package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.GmInventoryItem
import fr.manyverse.app.domain.model.GmInventoryItemToCreate
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.model.MemberRole
import fr.manyverse.app.domain.repository.ImageStorageRepository
import fr.manyverse.app.domain.usecase.CreateGmInventoryItemUseCase
import fr.manyverse.app.domain.usecase.DeleteGmInventoryItemImageUseCase
import fr.manyverse.app.domain.usecase.DeleteGmInventoryItemUseCase
import fr.manyverse.app.domain.usecase.GetCharacterByIdUseCase
import fr.manyverse.app.domain.usecase.GetGmInventoryItemsUseCase
import fr.manyverse.app.domain.usecase.NotifyItemSentUseCase
import fr.manyverse.app.domain.usecase.ObserveGameTableMembersUseCase
import fr.manyverse.app.domain.usecase.SendGmInventoryItemToPlayersUseCase
import fr.manyverse.app.domain.usecase.UpdateGmInventoryItemUseCase
import fr.manyverse.app.domain.usecase.UploadGmInventoryItemImageUseCase
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch

class GmInventoryViewModel(
    private val tableId: String,
    private val gmUserId: String,
    private val getGmInventoryItemsUseCase: GetGmInventoryItemsUseCase,
    private val createGmInventoryItemUseCase: CreateGmInventoryItemUseCase,
    private val updateGmInventoryItemUseCase: UpdateGmInventoryItemUseCase,
    private val deleteGmInventoryItemUseCase: DeleteGmInventoryItemUseCase,
    private val uploadGmInventoryItemImageUseCase: UploadGmInventoryItemImageUseCase,
    private val deleteGmInventoryItemImageUseCase: DeleteGmInventoryItemImageUseCase,
    private val sendGmInventoryItemToPlayersUseCase: SendGmInventoryItemToPlayersUseCase,
    private val notifyItemSentUseCase: NotifyItemSentUseCase,
    private val observeGameTableMembersUseCase: ObserveGameTableMembersUseCase,
    private val getCharacterByIdUseCase: GetCharacterByIdUseCase,
    private val imageStorageRepository: ImageStorageRepository,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    private val _itemsState = MutableStateFlow<AsyncUiState<List<GmInventoryItem>>>(AsyncUiState.Idle)
    val itemsState: StateFlow<AsyncUiState<List<GmInventoryItem>>> = _itemsState.asStateFlow()

    private val _createItemState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val createItemState: StateFlow<AsyncUiState<Unit>> = _createItemState.asStateFlow()

    private val _updateItemState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val updateItemState: StateFlow<AsyncUiState<Unit>> = _updateItemState.asStateFlow()

    private val _deleteItemState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val deleteItemState: StateFlow<AsyncUiState<Unit>> = _deleteItemState.asStateFlow()

    private val _sendItemState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val sendItemState: StateFlow<AsyncUiState<Unit>> = _sendItemState.asStateFlow()

    private val _playerCharacters = MutableStateFlow<List<Character>>(emptyList())
    val playerCharacters: StateFlow<List<Character>> = _playerCharacters.asStateFlow()

    init {
        loadItems()
        viewModelScope.launch {
            observeGameTableMembersUseCase(tableId)
                // Sans la liste des joueurs, le MJ ne peut envoyer aucun objet : il doit le savoir
                .catch { errorNotifier.notify(it) }
                .collect { members ->
                    val characters = members
                        .filter { it.role == MemberRole.PLAYER && it.characterId != null }
                        .mapNotNull { member ->
                            // En cas d'échec, la liste déjà connue reste affichée plutôt qu'amputée
                            getCharacterByIdUseCase(member.characterId!!).getOrElse { return@collect }
                        }
                    _playerCharacters.value = characters
                }
        }
    }

    fun loadItems() {
        launchAsync(_itemsState) {
            getGmInventoryItemsUseCase(tableId)
        }
    }

    fun createItem(
        name: String,
        category: ItemCategory,
        quantity: Int,
        imageData: ImageData? = null
    ) {
        launchAsyncWithCallback(
            state = _createItemState,
            block = {
                val itemToCreate = GmInventoryItemToCreate(
                    tableId = tableId,
                    name = name,
                    category = category,
                    quantity = quantity
                )
                val createResult = createGmInventoryItemUseCase(itemToCreate)
                if (createResult.isFailure) return@launchAsyncWithCallback createResult.map { }

                val createdItem = createResult.getOrThrow()
                if (imageData != null) {
                    val uploadResult = uploadGmInventoryItemImageUseCase(tableId, createdItem.id, imageData)
                    if (uploadResult.isFailure) return@launchAsyncWithCallback uploadResult.map { }
                }
                Result.success(Unit)
            },
            onSuccess = { loadItems() }
        )
    }

    fun updateItem(
        item: GmInventoryItem,
        name: String,
        category: ItemCategory,
        quantity: Int,
        imageData: ImageData? = null
    ) {
        launchAsyncWithCallback(
            state = _updateItemState,
            block = {
                val updateResult = updateGmInventoryItemUseCase(item.id, name, category, quantity)
                if (updateResult.isFailure) return@launchAsyncWithCallback updateResult.map { }

                if (imageData != null) {
                    val uploadResult = uploadGmInventoryItemImageUseCase(tableId, item.id, imageData)
                    if (uploadResult.isFailure) return@launchAsyncWithCallback uploadResult.map { }
                }
                Result.success(Unit)
            },
            onSuccess = { loadItems() }
        )
    }

    fun deleteItem(item: GmInventoryItem) {
        launchAsyncWithCallback(
            state = _deleteItemState,
            block = { deleteGmInventoryItemUseCase(item).map { } },
            onSuccess = { loadItems() }
        )
    }

    fun sendToPlayers(item: GmInventoryItem, targetCharacterIds: List<String>, quantity: Int) {
        launchAction(_sendItemState) {
            sendGmInventoryItemToPlayersUseCase(item, targetCharacterIds, quantity).onSuccess {
                // Les objets sont déjà envoyés : un échec de la notification est ignoré
                val recipients = _playerCharacters.value.filter { it.id in targetCharacterIds }
                if (recipients.isNotEmpty()) {
                    notifyItemSentUseCase(
                        tableId = tableId,
                        triggeredBy = gmUserId,
                        recipientUserIds = recipients.map { it.userId },
                        senderName = null,
                        recipientNames = recipients.map { it.name },
                        itemName = item.name,
                        quantity = quantity
                    )
                }
            }
        }
    }

    suspend fun getSignedImageUrl(imagePath: String): Result<String> {
        return imageStorageRepository.getSignedUrl(imagePath)
    }

    fun resetCreateItemState() { resetState(_createItemState) }
    fun resetUpdateItemState() { resetState(_updateItemState) }
    fun resetDeleteItemState() { resetState(_deleteItemState) }
    fun resetSendItemState() { resetState(_sendItemState) }
}
